import { defineStore } from 'pinia';
import type {
  IConfigState,
  IUpdateConfigState,
} from '../types/store-states.types';
import { EditorConfigModel } from '../models/editor-config.model';
import getSymbolFromCurrency from 'currency-symbol-map';
import uuid4 from '../utils/uuid4';
import type { ILineItemUpdate } from '../types/line-item.types';
import { useStepsStore } from './steps.store';
import StorefrontProductService from '../services/storefront-product.service';
import DesignAtomsService from '../services/design-atoms.service';


export const useConfigStore = defineStore('au-config', {
  state: () => ({
    designEditorUrl: null,
    productDefinition: null,
    editorConfig: null,
    user: null,
    backOfficeUrl: null,
    apiGatewayUrl: null,
    backEvent: null,
    currency: null,
    tenantId: null,
    breakPoints: null,
    steps: null,
    useCustomNextEvent: null,
    useHistory: null,
    toStepButtons: null,
    video: {
      src: null,
    },
    pim: {
      productId: null,
      productVersionId: null,
    },
    product: {
      title: null,
      type: null,
      quantity: null,
      originalProductId: null,
      originalProductUrl: null,
      sku: null,
    },
    buttons: {
      next: null,
      back: null,
      finish: null,
      duplicate: null,
      video: null,
    },
    approval: {
      checkboxList: null,
    },
  } as IConfigState),

  getters: {
    getEditorUrl: (state) => state.designEditorUrl,
    getProductDefinition: (state) => state.productDefinition,
    getEditorConfig: (state) =>
      new EditorConfigModel(state.editorConfig, state.user).config,
    getBackEvent: (state) => state.backEvent,
    getProduct: (state) => state.product,
    getBrakePoints: (state) => state.breakPoints,
    getVideoData: (state) => state.video,
    getButtonTitles: (state) => {
      const defaultNames: Record<keyof IConfigState['buttons'], string> = {
        next: 'Next',
        back: 'Back',
        finish: 'Continue',
        duplicate: 'Duplicate design',
        video: 'Watch tutorial',
      };
      const buttonsMap = Object.entries(state.buttons).map(
        ([key, value]) => {
          const buttonKey = key as keyof IConfigState['buttons'];
          return { [buttonKey]: value?.name || defaultNames[buttonKey] };
        }
      );
      return Object.assign({}, ...buttonsMap) as Record<keyof IConfigState['buttons'], string | undefined>;
    },
    getToStepButtonsList: (state) => state.toStepButtons,
    getCurrency: (state) =>
      state.currency
        ? getSymbolFromCurrency(state.currency)
        : getSymbolFromCurrency('USD'),
    getApprovalCheckboxList: (state) => state.approval.checkboxList,
    getUser: (state) => state.user,
    getTenantId: (state) => state.tenantId,
    getPIM: (state) => state.pim,
    getUseCustomNextEvent: (state) => !!state.useCustomNextEvent,
    getInitDataForLineItem: (state) =>
      ({
        key: uuid4(),
        originalProductId: state.product.originalProductId,
        originalProductUrl: state.product.originalProductUrl,
        sku: state.product.sku,
        quantity: state.product.quantity,
        userId: state.user?.id,
      } as ILineItemUpdate),
    getBackOfficeUrl: (state) => state.backOfficeUrl,
    getApiGatewayUrl: (state) => state.apiGatewayUrl,
    getUseHistory: (state) => state.useHistory,
  },

  actions: {
    updateConfig(config: IUpdateConfigState): void {
      const stepsStore = useStepsStore();

      const simpleFields = [
        'designEditorUrl',
        'productDefinition',
        'user',
        'editorConfig',
        'backEvent',
        'currency',
        'steps',
        'backOfficeUrl',
        'apiGatewayUrl',
        'tenantId',
        'breakPoints',
        'toStepButtons',
        'useCustomNextEvent',
        'useHistory',
        'video',
      ] as const;

      for (const field of simpleFields) {
        if (config[field] !== undefined) {
          this[field] = config[field];
        }
      }

      if (config.product) Object.assign(this.product, config.product);
      if (config.buttons) Object.assign(this.buttons, config.buttons);
      if (config.approval) Object.assign(this.approval, config.approval);
      if (config.pim) Object.assign(this.pim, config.pim);

      if (config.steps) {
        stepsStore.protectedInitializationSteps(config.steps);
      }
    },

    async fetchVariantDesign() {
      const apigateway = this.getApiGatewayUrl;
      const tenantId = this.getTenantId;
      const { productId, productVersionId } = this.getPIM;
      const { sku } = this.getProduct;
      const user = this.getUser;

      const storefrontProductService = new StorefrontProductService({
        apigateway,
        productId,
        tenantId,
        productVersionId,
        sku,
        token: user?.token || null,
      });

      return await storefrontProductService.getVariantDesign();
    },

    async setupGalleryRootFolder() {
      const apigateway = this.getApiGatewayUrl;
      const tenantId = this.getTenantId;
      const { productId, productVersionId } = this.getPIM;
      const { sku } = this.getProduct;
      const user = this.getUser;

      const storefrontProductService = new StorefrontProductService({
        apigateway,
        productId,
        tenantId,
        productVersionId,
        sku,
        token: user?.token || null,
      });

      const summary = await storefrontProductService.getProductSummary();
      
      const summaryEntry = Object.entries(summary.customFields);
      if (!summaryEntry?.length) {
        console.warn('There are no custom fields defined for the product.');
        return false;
      } 

      const galleryRootFolderEntry = summaryEntry.find(([key, _]) => key === 'Gallery Root Folder');
      if (!galleryRootFolderEntry) {
        console.warn('The product does not have a custom field named "Gallery Root Folder" defined.');
        return false;
      }

      const galleryRootFolder = galleryRootFolderEntry[1];

      if (!galleryRootFolder) {
        console.warn(`The product's custom field named "Gallery Root Folder" has an invalid value.`);
        return false;
      }

      this.editorConfig['editorConfig'] ??= {};
      this.editorConfig['editorConfig'].assetSources ??= {};
      this.editorConfig['editorConfig'].assetSources['Clipart'] ??= {};
      this.editorConfig['editorConfig'].assetSources['Clipart'].rootCategory = {
        name: galleryRootFolder,
      };

      return true;
    },

    async setupCustomButtonsFromSurfaceData() {
      const apigateway = this.getApiGatewayUrl;
      const tenantId = this.getTenantId;
      const user = this.getUser;

      const variantDesignResponse = await this.fetchVariantDesign();
      
      const publicStateId = variantDesignResponse.items[0].designId;

      const designAtomsService = new DesignAtomsService({
        apigateway,
        stateId: publicStateId,
        token: user?.token || null,
        tenantId,
        userId: user?.id || null,
      });

      const variables = await designAtomsService.getVariables();
      const variableList = variables.items.reverse();

      this.editorConfig['editorConfig'].widgets ??= {};
      this.editorConfig['editorConfig'].widgets.Toolbox ??= {};
      this.editorConfig['editorConfig'].widgets.Toolbox.buttons ??= [];

      const insertData = {
        buttons: [] as any[],
        iconClass: 'cc-icon-variables',
        translationKey: 'Variable',
        translationKeyTitle: 'Editable',
      };

      const schema = {
        action: 'ACTION_NAME',
        iconClass: 'cc-icon-variables',
        translationKey: 'ITEM_NAME',
        translationKeyTitle: 'ITEM_NAME',
        itemConfig: {
          name: 'ITEM_NAME',
          isVariable: true,
          text: 'ITEM_NAME',
          verticalAlignment: 'center',
          alignment: 'center',
          textPermissions: {
            allowChangeText: true,
            allowChangeFontSize: true,
          },
          manipulationPermissions: {
            resizeGrips: {
              edge: false,
              corner: [],
            },
          },
        },
      };
      
      const capitalize = (str: string, lower = false) => (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());

      insertData.buttons = variableList.map((variable) => {
        const sculp: typeof schema = JSON.parse(JSON.stringify(schema));
        sculp.action = capitalize(variable.type);
        sculp.translationKey = variable.name;
        sculp.translationKeyTitle = variable.name;
        sculp.itemConfig.name = variable.name;
        sculp.itemConfig.text = variable.name;
        return sculp;
      }).filter(Boolean);
      
      this.editorConfig['editorConfig'].widgets.Toolbox.buttons.push(insertData);
    },
  },
});
